import { useUser } from "@clerk/clerk-react";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEnrollment } from "@/contexts/EnrollmentContext";
import { api } from "@/services/api";

export const PENDING_CODE_KEY = "pending_enrollment_code";

type RedeemState = "idle" | "redeeming" | "success" | "error";

export default function RedeemPage() {
  const { isLoaded, isSignedIn } = useUser();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refetch: refetchEnrollment } = useEnrollment();

  const [state, setState] = useState<RedeemState>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const attempted = useRef(false);

  // Persist the code so it survives the Clerk sign-up/sign-in redirect.
  const codeFromUrl = searchParams.get("code");
  useEffect(() => {
    if (codeFromUrl) {
      sessionStorage.setItem(PENDING_CODE_KEY, codeFromUrl);
    }
  }, [codeFromUrl]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || attempted.current) return;
    const code = codeFromUrl || sessionStorage.getItem(PENDING_CODE_KEY);
    if (!code) {
      setState("error");
      setErrorMessage("No enrollment code provided.");
      return;
    }

    attempted.current = true;
    setState("redeeming");
    (async () => {
      try {
        const result = await api.redeemEnrollmentCode(code);
        sessionStorage.removeItem(PENDING_CODE_KEY);
        await refetchEnrollment();
        setState("success");
        setTimeout(() => navigate("/my-programmes", { replace: true }), 1500);
        void result;
      } catch (err) {
        sessionStorage.removeItem(PENDING_CODE_KEY);
        setState("error");
        setErrorMessage(
          err instanceof Error
            ? err.message
            : "We couldn't redeem this code. Please contact support."
        );
      }
    })();
  }, [isLoaded, isSignedIn, codeFromUrl, navigate, refetchEnrollment]);

  return (
    <div className="min-h-screen bg-sidebar flex items-center justify-center p-4">
      <div className="bg-white/10 backdrop-blur-sm p-12 rounded-2xl shadow-xl max-w-md w-full border border-white/10 text-center">
        <div className="mb-6 flex justify-center">
          <img src="/dfr-logo.svg" alt="DFR Logo" className="h-12 w-auto" />
        </div>

        {!isLoaded && <Loader2 className="w-6 h-6 animate-spin text-white mx-auto" />}

        {isLoaded && !isSignedIn && (
          <>
            <h1 className="text-2xl font-bold text-white mb-2">You're almost in</h1>
            <p className="text-white/60 mb-8">
              Your seat is reserved. Create your account (or sign in) to redeem your code and access
              the programme.
            </p>
            <Link
              to="/sign-up"
              className="block w-full bg-white text-sidebar px-6 py-3 rounded-xl font-bold hover:bg-white/90 transition-colors mb-3"
            >
              Create account
            </Link>
            <Link
              to="/sign-in"
              className="block w-full bg-transparent border border-white/30 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/10 transition-colors"
            >
              Sign in
            </Link>
          </>
        )}

        {isLoaded && isSignedIn && state === "redeeming" && (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-white mx-auto mb-4" />
            <p className="text-white/80">Redeeming your code…</p>
          </>
        )}

        {isLoaded && isSignedIn && state === "success" && (
          <>
            <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">You're enrolled!</h1>
            <p className="text-white/60">Taking you to your programme…</p>
          </>
        )}

        {isLoaded && isSignedIn && state === "error" && (
          <>
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Couldn't redeem code</h1>
            <p className="text-white/60 mb-8">{errorMessage}</p>
            <Link
              to="/"
              className="block w-full bg-white text-sidebar px-6 py-3 rounded-xl font-bold hover:bg-white/90 transition-colors"
            >
              Go to dashboard
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
